import styled from '@emotion/styled/macro'
import { Box } from '@mui/material'
import theme from 'theme'

export const BenchmarkingWrapper = styled(Box)`
  background: ${theme.colors.surfaceContainerBackground};
  border-radius: ${theme.attributes.borderRadius.tertiary};
  display: flex;
  flex-direction: column;
  flex: 1;
  /* Allow this flex child to shrink past its intrinsic content width.
     Without min-width:0, browsers fall back to min-width:auto and this
     card gets pushed off-screen on zoom-in. */
  min-width: 0;
  height: 649px;
  box-sizing: border-box;

  .benchmarking-top {
    display: flex;
    padding: ${theme.spacing.medium}px;
    width: 100%;
    height: 242px;
    min-height: 242px;
    flex-shrink: 0;
    box-sizing: border-box;

    .top-content {
      display: flex;
      flex-direction: column;
      gap: 48px;
      flex: 1;

      .header-row {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        width: 100%;

        .header-left {
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing.xSmall}px;

          .title-row {
            display: flex;
            align-items: center;
            gap: 4px;

            .title-icon {
              width: 18px;
              height: 18px;
              color: ${theme.colors.menuItemText};
            }

            .title-text {
              font-family: ${theme.typography.fontFamily.primary};
              font-weight: ${theme.typography.fontWeight.bold};
              font-size: ${theme.typography.fontSize.header};
              color: ${theme.colors.menuItemText};
            }

            .info-icon-wrapper {
              display: flex;
              cursor: pointer;
              color: #6c5b67;

              &:hover {
                color: ${theme.colors.buttonHollowHover};
              }
            }
          }

          .last-update {
            font-family: ${theme.typography.fontFamily.primary};
            font-weight: ${theme.typography.fontWeight.normal};
            font-size: ${theme.typography.fontSize.large};
            color: ${theme.colors.dropdownLabel};
            line-height: 22px;

            html[data-theme='light'] & {
              color: ${theme.colors.textCaption};
            }

            .date-value {
              color: ${theme.colors.menuItemText};
            }
          }
        }
      }

      .stacked-bar-section {
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing.xSmall}px;
        flex: 1;

        .bar-labels {
          display: flex;
          font-family: ${theme.typography.fontFamily.primary};
          font-weight: ${theme.typography.fontWeight.bold};
          font-size: ${theme.typography.fontSize.large};
          color: ${theme.colors.devicesSecondaryText};
          line-height: 22px;
        }

        .stacked-bar {
          display: flex;
          height: 57px;
          border-radius: ${theme.attributes.borderRadius.small};
          overflow: hidden;

          .bar-segment {
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: ${theme.attributes.borderRadius.small};
            transition: opacity 0.15s ease;

            &.less-strict {
              background: ${theme.colors.chartSeries1};
            }

            &.same-sla {
              background: ${theme.colors.chartSeries2};
            }

            &.more-strict {
              background: ${theme.colors.benchMoreStrict};
            }
          }
        }
      }

      .empty-bar {
        height: 57px;
        border-radius: ${theme.attributes.borderRadius.small};
        background: ${theme.colors.borderMedium};
      }
    }
  }

  .benchmarking-bottom {
    border-top: 1px solid ${theme.colors.borderMedium};
    padding: ${theme.spacing.medium}px;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.medium}px;
    flex: 1;

    .chart-controls {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: ${theme.spacing.small}px;
    }

    .history-last-update {
      font-family: ${theme.typography.fontFamily.primary};
      font-weight: ${theme.typography.fontWeight.normal};
      font-size: ${theme.typography.fontSize.large};
      color: ${theme.colors.dropdownLabel};
      line-height: 22px;

      html[data-theme='light'] & {
        color: ${theme.colors.textCaption};
      }

      .date-value {
        color: ${theme.colors.menuItemText};
      }
    }

    .empty-state {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      max-width: 294px;
      margin: 0 auto;
      text-align: center;

      svg {
        margin-bottom: 10px;
      }

      .empty-title {
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.bold};
        font-size: ${theme.typography.fontSize.xLarge};
        color: ${theme.colors.menuItemText};
        line-height: 20px;
        letter-spacing: 0.016px;
      }

      .empty-description {
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.normal};
        font-size: ${theme.typography.fontSize.large};
        color: ${theme.colors.devicesSecondaryText};
        line-height: 20px;
      }
    }

    .chart-container {
      flex: 1;
      width: 100%;
      min-height: 200px;

      .recharts-cartesian-axis-tick text,
      .recharts-cartesian-axis-tick tspan {
        fill: ${theme.colors.chartAxisText} !important;
        color: ${theme.colors.chartAxisText} !important;
      }

      /* Let edge tick labels render past the SVG bounds into the surrounding
         padding instead of getting clipped. */
      .recharts-surface {
        overflow: visible;
      }
    }

    .legend {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 54px;

      .legend-item {
        display: flex;
        align-items: center;
        gap: ${theme.spacing.sm}px;

        .legend-color {
          width: 12px;
          height: 12px;
          border-radius: 2px;

          &.less-strict {
            background: ${theme.colors.chartSeries1};
          }

          &.same-sla {
            background: ${theme.colors.chartSeries2};
          }

          &.more-strict {
            background: ${theme.colors.benchMoreStrict};
          }
        }

        .legend-label {
          font-family: ${theme.typography.fontFamily.primary};
          font-weight: ${theme.typography.fontWeight.bold};
          font-size: ${theme.typography.fontSize.large};
          color: ${theme.colors.menuItemText};
          letter-spacing: 0.5px;
          line-height: 22px;
        }
      }
    }
  }
`
